import {
  Box,
  BoxProps,
  Card as MuiCard,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import React from "react";

import { resolveIcon } from "../utils";

export interface PMBaseCardProps {
  /**
   * Icon to display in the header. If you pass a string it must match a key
   * from `@mui/icons-material` (e.g. `"Home"`, `"InfoOutlined"`).
   * Otherwise, you can pass any React node.
   */
  icon?: React.ReactNode | string;

  /**
   * The card title. Can be a string or a custom React node.
   */
  title?: string | React.ReactNode;

  /**
   * Whether to display the info button in the header.
   * @default true
   */
  showInfoIcon?: boolean;

  /**
   * Icon to use for the info button. String keys are resolved against
   * `@mui/icons-material` (e.g. `"InfoOutlined"`).
   * @default "InfoOutlined"
   */
  infoIcon?: React.ReactNode | string;

  /**
   * Whether to display the menu button in the header.
   * @default true
   */
  showMenuIcon?: boolean;

  /**
   * Icon to use for the menu button. String keys are resolved against
   * `@mui/icons-material` (e.g. `"MoreVert"`).
   * @default "MoreVert"
   */
  menuIcon?: React.ReactNode | string;

  /**
   * Width of the card. If you pass a number, it’s treated as pixels.
   * @default "300px"
   */
  fixedWidth?: number | string;

  /**
   * Height of the card. If you pass a number, it's treated as pixels.
   * @default "150px"
   */
  fixedHeight?: number | string;

  /**
   * Overflow behavior for the card content.
   * @default "hidden"
   */
  overflow?: "visible" | "hidden" | "auto" | "scroll";

  /**
   * A Override of the right-hand slot, to add custom buttons or other components.
   */
  rightSlotOverride?: React.ReactNode;

  /**
   * The content to render inside the body of the card.
   */
  children?: React.ReactNode;

  /**
   * Padding applied around the body content, using the MUI spacing scale
   * (e.g. `3` = 24px).
   * @default 2
   */
  contentPadding?: number;

  style?: BoxProps;
}

/**
 * A flexible card component that renders:
 *  - An optional left-aligned icon and title
 *  - Optional right-aligned info and menu buttons
 *  - A body area for children content
 *
 * @param props.icon               Icon node or string key to resolve from MUI Icons.
 * @param props.title              Title text or custom node.
 * @param props.showInfoIcon       Whether to display the info button. @default true
 * @param props.infoIcon           Icon node or string key for the info button. @default "InfoOutlined"
 * @param props.showMenuIcon       Whether to display the menu button. @default true
 * @param props.menuIcon           Icon node or string key for the menu button. @default "MoreVert"
 * @param props.fixedWidth         Width of the card; numbers are in px. @default "300px"
 * @param props.fixedHeight        Height of the card; numbers are in px. @default "150px"
 * @param props.overflow           Overflow behavior for the card content. @default "hidden"
 * @param props.rightSlotOverride  An override for the right-hand side slot of the title bar.
 * @param props.children           Body content of the card.
 * @param props.contentPadding     Padding around the body content, MUI spacing units. @default 2
 * @param props.style              Additional styles for the card container.
 * @returns A MUI Card component styled with header and body slots.
 */
export const PMBaseCard: PimoReactComponent<PMBaseCardProps> = ({
  icon,
  title,
  showInfoIcon = true,
  infoIcon,
  showMenuIcon = true,
  menuIcon,
  fixedWidth = "100%",
  fixedHeight = "150px",
  overflow = "hidden",
  rightSlotOverride,
  children,
  style,
  contentPadding = 2,
  ...props
}) => {
  const resolvedIcon = resolveIcon(icon);
  const resolvedInfoIcon = resolveIcon(infoIcon || "InfoOutlined");
  const resolvedMenuIcon = resolveIcon(menuIcon || "MoreVert");

  const theme = useTheme();

  const leftSlot = (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        mt: 2,
      }}
    >
      {resolvedIcon && (
        <Box sx={{ display: "flex", alignItems: "center", mr: "4px" }}>
          {resolvedIcon}
        </Box>
      )}
      {title &&
        (typeof title === "string" ? (
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {title}
          </Typography>
        ) : (
          title
        ))}
    </Box>
  );

  const rightSlot = rightSlotOverride ?? (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {showInfoIcon && resolvedInfoIcon && (
        <IconButton size="small" sx={{ p: 0, m: 0 }}>
          {resolvedInfoIcon}
        </IconButton>
      )}
      {showMenuIcon && resolvedMenuIcon && (
        <IconButton size="small" sx={{ p: 0, m: 0 }}>
          {resolvedMenuIcon}
        </IconButton>
      )}
    </Box>
  );

  return (
    <MuiCard
      {...props}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 6,
        boxShadow: "none",
        width: fixedWidth,
        height: fixedHeight,
        overflow: overflow,
        ...style,
      }}
    >
      {(resolvedIcon || title || showInfoIcon || showMenuIcon) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            px: 2,
            pt: 0,
            mt: 0,
            maxHeight: 60,
            minHeight: 60,
          }}
        >
          {leftSlot}
          {rightSlot}
        </Box>
      )}
      <Box sx={{ flex: 1, p: contentPadding, minWidth: 0 }}>{children}</Box>
    </MuiCard>
  );
};

export default PMBaseCard;
